import { StatusTag } from '@/pages/price-planning/simulation/simulation-list-page/components/status-tag';
import { themeColors } from '@/theme/colors';
import { Avatar, Button } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useState } from 'react';
import type { VersionStatus } from '../utils/simulation-types';

export type VersionCardProps = {
  status: VersionStatus;
  previousStatus?: VersionStatus;
  actorName: string;
  actionLabel: string;
  timeLabel: string;
  previousAssigneeName?: string;
  assigneeName?: string;
  message?: string | null;
  isSelected?: boolean;
  onSelect?: () => void;
};

export const VersionCard = ({
  status,
  previousStatus,
  actorName,
  actionLabel,
  timeLabel,
  previousAssigneeName,
  assigneeName,
  message,
  isSelected = false,
  onSelect,
}: VersionCardProps) => {
  const [isCommentExpanded, setIsCommentExpanded] = useState(false);
  const hasAssigneeChange =
    previousAssigneeName != null &&
    assigneeName != null &&
    previousAssigneeName !== assigneeName;
  const hasStatusChange = previousStatus != null && previousStatus !== status;
  const shouldTruncateComment = (message?.length ?? 0) > 140;
  const hasExpandedDetails =
    hasAssigneeChange || hasStatusChange || message != null;
  const visibleMessage =
    shouldTruncateComment && !isCommentExpanded
      ? `${message?.slice(0, 140)}...`
      : message;

  const timelineDetails = (
    <>
      <Flex alignItems="flex-start" justifyContent="space-between" gap="8px">
        <Flex alignItems="flex-start" gap="6px" minWidth={0}>
          <Avatar
            name={actorName}
            iconName="user"
            style={{ height: '20px', width: '20px' }}
          />
          <Text className="body-sm-medium" color={themeColors.content.default}>
            {actorName} {actionLabel}
          </Text>
        </Flex>
        {!hasExpandedDetails && (
          <Flex alignItems="center" flexShrink={0} gap="8px">
            <Text
              className="body-sm-regular"
              color={themeColors.content.subtle}
            >
              {timeLabel}
            </Text>
            <StatusTag status={status} size="small" />
          </Flex>
        )}
      </Flex>
      {hasExpandedDetails && (
        <>
          <Text
            className="body-sm-regular"
            color={themeColors.content.subtle}
            alignSelf="flex-start"
          >
            {timeLabel}
          </Text>
          {hasAssigneeChange && (
            <Text
              className="body-sm-regular"
              color={themeColors.content.default}
            >
              from {previousAssigneeName} to {assigneeName}
            </Text>
          )}
          <Flex alignItems="center" gap="6px" flexWrap="wrap">
            {hasStatusChange && (
              <StatusTag status={previousStatus} size="small" />
            )}
            {hasStatusChange && (
              <Text
                className="body-sm-regular"
                color={themeColors.content.default}
              >
                to
              </Text>
            )}
            <StatusTag status={status} size="small" />
          </Flex>
        </>
      )}
    </>
  );

  return (
    <Flex
      as="button"
      type="button"
      aria-label={`${actorName} ${actionLabel}`}
      flexDirection="column"
      gap="6px"
      padding="12px"
      borderRadius="8px"
      textAlign="left"
      border={`2px solid ${
        isSelected
          ? themeColors.support.primary.default
          : themeColors.stroke.subtle
      }`}
      backgroundColor={
        isSelected
          ? themeColors.interactive.primary.subtle.selected
          : themeColors.surface.background.light
      }
      transition="background-color 150ms ease"
      cursor="pointer"
      onClick={onSelect}
      _hover={
        !isSelected
          ? { backgroundColor: themeColors.interactive.neutral.subtle.hovered }
          : undefined
      }
      _focusVisible={{
        outline: `2px solid ${themeColors.support.primary.default}`,
        outlineOffset: '2px',
      }}
    >
      <Flex flexDirection="column" gap="6px">
        {timelineDetails}
      </Flex>
      {message && (
        <Flex flexDirection="column" gap="4px">
          <Text className="body-sm-medium" color={themeColors.content.default}>
            Comment added:
          </Text>
          <Text
            className="body-sm-regular"
            color={themeColors.content.default}
            padding="8px"
            borderRadius="var(--ds-layout-radius-75)"
            backgroundColor={themeColors.surface.background.base.muted}
          >
            {visibleMessage}
          </Text>
          {shouldTruncateComment && (
            <Button
              aria-label={
                isCommentExpanded ? 'Collapse comment' : 'Expand comment'
              }
              appearance="ghost"
              size="small"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setIsCommentExpanded((expanded) => !expanded);
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              {isCommentExpanded ? 'Read less' : 'Read more'}
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
};
